import {difference, intersection} from 'ramda';
import {capitalizeEachWord, Options, Validator} from '../Util';
import {getName, Paginate, Paginated} from './Common';
import Constants from './Constants';
import {emailAddressesToString, isSendEmailEvent} from './Emails';
import {remoovNodeEndpoint} from './EnvVar';
import {Event} from './Events';
import {DEFAULT_MARKET, MARKETS} from './Markets';
import {StaffIncident} from './Staff';
const {
  DashboardUrl,
  CustomerPortalUrl,
  Ability,
  Ability: {
    ADMIN,
    ESTIMATOR,
    TAGGER,
    CURATOR,
    PHOTOGRAPHER,
    PHOTO_EDITOR,
    WORKER,
    CUSTOMER_SERVICE,
    POSTER,
    OWNER,
    EMPLOYEE,
    TESTER,
  },
} = Constants;

export type UserApi = {
  loadUser: (a: UserSearch) => SavedUser;
  loadUsers: (a?: UserSearch & Paginate) => Paginated<'users', SavedUser[]>;
  loadUserEmails: () => string[];
  loadUserPasscodes: () => string[];
  loadUserOptions: (a?: UserSearch & Paginate) => Options<Maybe<number>>;
  saveUser: (a: Partial<User>) => number;
};

export {Ability};
export const INTERNAL_WEBSITE_ABILITIES = [
  ADMIN,
  ESTIMATOR,
  TAGGER,
  CURATOR,
  PHOTOGRAPHER,
  PHOTO_EDITOR,
  CUSTOMER_SERVICE,
  POSTER,
  OWNER,
];
export const OPERATIONS_MANAGER = 'Operations Manager';
export const EMPLOYEE_ABILITIES = difference(Object.values(Ability), [OWNER, WORKER]);

export const EMPTY_STAFF_INCIDENT = {
  timestamp: null,
  data: '',
  files: [],
  created_at: null,
  created_by: null,
  deleted_at: null,
};
export type User = {
  id: Maybe<number>;
  first_name: Maybe<string>;
  last_name: Maybe<string>;
  email: Maybe<string>;
  sign_in_count: Maybe<number>;
  blocked: Maybe<boolean>;
  current_sign_in_at: Maybe<string>;
  last_sign_in_at: Maybe<string>;
  created_at: Maybe<string>;
  updated_at: Maybe<string>;
  app_passcode: Maybe<string>;
  owner_id: Maybe<number>;
  employee_id: Maybe<number>;
  name: string;
  abilities: string[];
  markets: string[];
  selectedMarkets: Maybe<string[]>;
  picture_url: Maybe<string>;
  phone: Maybe<string>;
  inactive_reason: Maybe<string>;
  available: Maybe<boolean>;
  notes: Maybe<string>;
  incidents: Maybe<StaffIncident[]>;
  end_date: Maybe<string>;
  files: {url: string; type: string}[];
  fcm_token: Maybe<string>;
  freshdesk_id: Maybe<string>;
  freshdesk_active: Maybe<boolean>;
  freshdesk_last_assigned_at: Maybe<string>;
  slack_id: Maybe<string>;
  // TODO: move this field to Context
  isLoginFake?: boolean;
};

export type Profile = User & {
  current_password?: string;
  password?: string;
  password_confirmation?: string;
};

export type SavedUser = Omit<User, 'id' | 'email'> & {
  id: number;
  email: string;
};

export const EMPTY_USER = {
  id: null,
  first_name: null,
  last_name: null,
  email: null,
  sign_in_count: 0,
  blocked: null,
  current_sign_in_at: null,
  last_sign_in_at: null,
  created_at: null,
  updated_at: null,
  app_passcode: null,
  owner_id: null,
  employee_id: null,
  name: 'No Name',
  abilities: [],
  markets: [],
  selectedMarkets: [],
  picture_url: null,
  phone: null,
  inactive_reason: null,
  available: false,
  notes: null,
  incidents: null,
  end_date: null,
  files: [],
  fcm_token: null,
  freshdesk_id: null,
  freshdesk_active: null,
  freshdesk_last_assigned_at: null,
  slack_id: null,
};
export const EMPTY_EMPLOYEE = {
  ...EMPTY_USER,
};
export const WORKER_USER = {
  ...EMPTY_USER,
  abilities: [WORKER],
  markets: MARKETS,
};
export const TESTER_USER = {
  ...EMPTY_USER,
  id: 1_000_000_000,
  first_name: 'Tester',
  last_name: 'User',
  email: 'testeraccount@remoovit.com',
  sign_in_count: 1,
  name: 'Tester User',
  abilities: [EMPLOYEE, TESTER],
  markets: [DEFAULT_MARKET],
  selectedMarkets: [DEFAULT_MARKET],
};
export const TESTER_USER_PASSWORD = 'test123123';
export type UserSearch = {
  id?: Maybe<OrArray<number>>;
  app_passcode?: Maybe<OrArray<string>>;
  access_token?: Maybe<OrArray<string>>;
  email?: Maybe<OrArray<string>>;
  first_name?: Maybe<OrArray<string>>;
  last_name?: Maybe<OrArray<string>>;
  markets?: Maybe<string[]>;
  abilities?: Maybe<string[]>;
  look_for?: Maybe<string>;
  sort_by?: Maybe<string>;
  paginate?: Maybe<{perPage: number; page: number}>;
  employee_id?: Maybe<OrArray<number>>;
  blocked?: boolean;
  owner_id?: Maybe<OrArray<number>>;
  freshdesk_id_not_null?: Maybe<boolean>;
  freshdesk_active?: Maybe<boolean>;
};

export function isUserValid(user: User, emails: string[] = [], passcodes: string[] = []): boolean {
  return Object.values(validateUser(user, emails, passcodes)).every(v => !v);
}
export function validateUser(
  user: User,
  emails: string[] = [],
  passcodes: string[] = [],
): {[P in keyof User]: Maybe<string>} {
  const firstNameError = Validator.required(user.first_name);
  const lastNameError = Validator.required(user.last_name);
  return {
    id: null,
    first_name: firstNameError,
    last_name: lastNameError,
    name: firstNameError
      ? 'first name is required'
      : lastNameError
      ? 'last name is required'
      : null,
    email: Validator.validateEmail(user.email, emails),
    sign_in_count: null,
    blocked: null,
    current_sign_in_at: null,
    last_sign_in_at: null,
    created_at: null,
    updated_at: null,
    app_passcode:
      user.app_passcode && !(user.app_passcode || '').match(/^\d\d\d\d$/)
        ? 'should have exactly 4 digits'
        : Validator.unique(user.app_passcode, passcodes),
    owner_id: null,
    employee_id: null,
    abilities: null,
    markets:
      isEmployee(user) && (!user.markets || user.markets.length === 0) ? 'is required' : null,
    selectedMarkets: null,
    picture_url: null,
    phone: null,
    inactive_reason: null,
    available: null,
    notes: null,
    incidents: null,
    end_date: null,
    files: null,
    fcm_token: null,
    freshdesk_id: null,
    freshdesk_active: null,
    freshdesk_last_assigned_at: null,
    slack_id: null,
  };
}
export function setUserName<
  T extends {
    first_name: Maybe<string>;
    last_name: Maybe<string>;
  },
>(user: T, name: Maybe<string>): T {
  const [first_name, ...family_names] = (name || '').split(' ');
  return {
    ...user,
    name,
    first_name,
    last_name: family_names.join(' '),
  };
}
export function restrictUserMarkets(
  {selectedMarkets}: User,
  markets: Maybe<string[]>,
): Maybe<string[]> {
  if (selectedMarkets && markets) return intersection(selectedMarkets, markets);
  return selectedMarkets || markets;
}
export function isEmployee(user: User): boolean {
  return Boolean(user.employee_id) || intersection(user.abilities, EMPLOYEE_ABILITIES).length > 0;
}
export function signInRedirect(user: User): Maybe<string> {
  if (user.blocked || intersection(user.abilities, INTERNAL_WEBSITE_ABILITIES).length === 0)
    return null;
  if (user.abilities.includes(OWNER))
    return `${remoovNodeEndpoint()}${CustomerPortalUrl.DASHBOARD_URL}`;
  if (user.abilities.includes(ADMIN)) return `${remoovNodeEndpoint()}${DashboardUrl.PICKUPS_URL}`;
  if (user.abilities.includes(ESTIMATOR))
    return `${remoovNodeEndpoint()}${DashboardUrl.PICKUPS_URL}`;
  if (user.abilities.includes(PHOTO_EDITOR))
    return `${remoovNodeEndpoint()}${DashboardUrl.PHOTO_EDITOR_QUEUE_URL}`;
  if (user.abilities.includes(TAGGER))
    return `${remoovNodeEndpoint()}${DashboardUrl.TAGGER_QUEUE_URL}`;
  if (user.abilities.includes(CURATOR))
    return `${remoovNodeEndpoint()}${DashboardUrl.CURATOR_QUEUE_URL}`;
  if (user.abilities.includes(POSTER)) return `${remoovNodeEndpoint()}${DashboardUrl.POSTINGS_URL}`;
  if (user.abilities.includes(PHOTOGRAPHER))
    return `${remoovNodeEndpoint()}${DashboardUrl.ITEMS_URL}`;
  if (user.abilities.includes(CUSTOMER_SERVICE))
    return `${remoovNodeEndpoint()}${DashboardUrl.OPERATIONS_DASHBOARD_URL}`;
  return null;
}
export function isFullName(name: Maybe<string>): boolean {
  if (!name?.trim()) return false;
  return name.trim().split(/\s+/).length >= 2;
}
export function splitName(name: Maybe<string>): {
  first_name: Maybe<string>;
  last_name: Maybe<string>;
} {
  const [first_name, ...last_name] = name?.split(' ') || [];
  return {
    first_name: name ? first_name : undefined,
    last_name: name ? last_name.join(' ') : undefined,
  };
}
export function marketToOpsManagerAbility(market: Maybe<string>): string {
  return market ? `${OPERATIONS_MANAGER} - ${market}` : '';
}
export function isAnyUserMarketManager(abilities: string[] | string): boolean {
  return Array.isArray(abilities)
    ? abilities.some(ability => ability.includes(OPERATIONS_MANAGER))
    : abilities.includes(OPERATIONS_MANAGER);
}

const AUTH_FAILURE_REASON_LABELS: Record<string, string> = {
  missing_credentials: 'Missing email or password',
  user_not_found: 'Email not registered',
  user_blocked: 'Account blocked',
  no_password_set: 'No password set',
  wrong_password: 'Wrong password',
  pending_balance: 'Pending balance on account',
  invalid_captcha: 'Captcha verification failed',
  expired_reset_token: 'Password reset link expired',
  invalid_reset_token: 'Invalid password reset link',
};

function getAuthFailureReason(event: Event): string {
  const failureReason =
    typeof event.json === 'object' &&
    event.json &&
    'failure_reason' in event.json &&
    typeof (event.json as {failure_reason?: string}).failure_reason === 'string'
      ? (event.json as {failure_reason: string}).failure_reason
      : '';
  return AUTH_FAILURE_REASON_LABELS[failureReason] || failureReason || 'Unknown';
}

export function userEventMessage(event: Event): string | null {
  const {event_type, user: eventUser, args} = event;
  if (!event_type || event.event_type === 'scheduleToggleUserFreshdeskActive') return null;
  const result = {
    signInAttemptFailed: () => `Sign in failed — ${getAuthFailureReason(event)}`,
    passwordResetFailed: () => `Password reset failed — ${getAuthFailureReason(event)}`,
    saveUser: () => `User updated by '${eventUser?.name || 'No name'}'`,
    saveStaff: () => `Staff profile updated by '${eventUser?.name || 'No name'}'`,
    updateUserProfile: () => 'Profile updated',
    toggleUserFreshdeskActive: () =>
      // @ts-ignore
      `${getName(args)} went ${args?.freshdesk_active ? 'Active' : 'Inactive'}`,
    assignFreshdeskTicket: () =>
      // @ts-ignore
      `Ticket #${args?.ticketId} assigned to ${getName(args)}`,
    saveUserFcmToken: () => 'Push notification token saved',
    removeUserFcmToken: () => 'Push notification token removed',
    sendEmail: () => {
      if (!isSendEmailEvent(event)) return null;
      const {email} = event.args;
      return `Email sent to '${emailAddressesToString(email.to)}' with subject '${email.subject}'`;
    },
    sendPushNotification: () => 'Push notification sent',
  }[event_type];
  // @ts-ignore
  if (result) return result();
  return capitalizeEachWord(event_type);
}
